import React from 'react';
import { AncientOneSelectionType, InvestigatorDrawType } from '../api';
import { ErrorMessage } from './ErrorMessage';
import { ExpansionToggles } from './ExpansionToggles';
import { RandomCluePlacementOption } from './RandomCluePlacementOption';

interface SoloViewProps {
  soloInvestigators: number;
  soloSeed: string;
  investigatorDrawType: InvestigatorDrawType;
  ancientOneSelectionType: AncientOneSelectionType;
  enableDunwichBoard?: boolean;
  enableKingsportBoard?: boolean;
  enableInnsmouthBoard?: boolean;
  onEnableDunwichBoardChange?: (value: boolean) => void;
  onEnableKingsportBoardChange?: (value: boolean) => void;
  onEnableInnsmouthBoardChange?: (value: boolean) => void;
  includeDunwichCards: boolean;
  includeDarkPharaohCards: boolean;
  includeKingsportCards: boolean;
  includeKingInYellowCards: boolean;
  includeBlackGoatCards: boolean;
  includeMiskatonicCards?: boolean;
  includeLurkerCards?: boolean;
  includeInsmouthCards: boolean;
  usePersonalStories: boolean;
  useNewGateMarkers?: boolean;
  includeFanMadeInvestigators: boolean;
  randomCluePlacement: boolean;
  showAdminOnly?: boolean;
  loading: boolean;
  error?: string | null;
  onInvestigatorsChange: (value: number) => void;
  onSeedChange: (value: string) => void;
  onDrawTypeChange: (value: InvestigatorDrawType) => void;
  onAncientOneSelectionTypeChange: (value: AncientOneSelectionType) => void;
  onIncludeDunwichCardsChange: (value: boolean) => void;
  onIncludeDarkPharaohCardsChange: (value: boolean) => void;
  onIncludeKingsportCardsChange: (value: boolean) => void;
  onIncludeKingInYellowCardsChange: (value: boolean) => void;
  onIncludeBlackGoatCardsChange: (value: boolean) => void;
  onIncludeMiskatonicCardsChange?: (value: boolean) => void;
  onIncludeLurkerCardsChange?: (value: boolean) => void;
  onIncludeInsmouthCardsChange: (value: boolean) => void;
  onUsePersonalStoriesChange: (value: boolean) => void;
  onUseNewGateMarkersChange?: (value: boolean) => void;
  onIncludeFanMadeInvestigatorsChange: (value: boolean) => void;
  onRandomCluePlacementChange: (value: boolean) => void;
  onCreate: () => void;
  onBack: () => void;
  isPrivate?: boolean;
  onPrivateChange?: (value: boolean) => void;
}

export const SoloView: React.FC<SoloViewProps> = ({
  soloInvestigators,
  soloSeed,
  investigatorDrawType,
  ancientOneSelectionType,
  enableDunwichBoard = true,
  enableKingsportBoard = true,
  enableInnsmouthBoard = true,
  onEnableDunwichBoardChange,
  onEnableKingsportBoardChange,
  onEnableInnsmouthBoardChange,
  includeDunwichCards,
  includeDarkPharaohCards,
  includeKingsportCards,
  includeKingInYellowCards,
  includeBlackGoatCards,
  includeMiskatonicCards = false,
  includeLurkerCards = false,
  includeInsmouthCards,
  usePersonalStories,
  useNewGateMarkers = true,
  includeFanMadeInvestigators,
  randomCluePlacement,
  showAdminOnly,
  loading,
  error,
  isPrivate,
  onPrivateChange,
  onInvestigatorsChange,
  onSeedChange,
  onDrawTypeChange,
  onAncientOneSelectionTypeChange,
  onIncludeDunwichCardsChange,
  onIncludeDarkPharaohCardsChange,
  onIncludeKingsportCardsChange,
  onIncludeKingInYellowCardsChange,
  onIncludeBlackGoatCardsChange,
  onIncludeMiskatonicCardsChange,
  onIncludeLurkerCardsChange,
  onIncludeInsmouthCardsChange,
  onUsePersonalStoriesChange,
  onUseNewGateMarkersChange,
  onIncludeFanMadeInvestigatorsChange,
  onRandomCluePlacementChange,
  onCreate,
  onBack,
}) => {
  return (
    <div className="App">
      <div className="solo-menu">
        <h2>Play solo <small>(control up to 8 investigators all by yourself)</small></h2>
        <label className='select-label'>
          Investigators:
          <select
            className="select-input"
            value={soloInvestigators || 1}
            onChange={e => onInvestigatorsChange(Number(e.target.value) || 1)}
          >
            <option value={1}>1</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
            <option value={4}>4</option>
            <option value={5}>5</option>
            <option value={6}>6</option>
            <option value={7}>7</option>
            <option value={8}>8</option>
          </select>
        </label>
        {/* <label>
          Seed (optional):
          <input
            type="number"
            value={soloSeed}
            onChange={e => onSeedChange(e.target.value)}
            placeholder="Random if empty"
          />
        </label> */}
        <label className='select-label'>
          Investigator draw:
          <select
            className="select-input"
            value={investigatorDrawType}
            onChange={e => onDrawTypeChange(e.target.value as InvestigatorDrawType)}
          >
            <option value="RandomDraft">Random draft (3 options)</option>
            <option value="Random">Random</option>
            <option value="Selection">Selection</option>
          </select>
        </label>
        <label className='select-label'>
          Ancient One draw:
          <select
            className="select-input"
            value={ancientOneSelectionType}
            onChange={e => onAncientOneSelectionTypeChange(e.target.value as AncientOneSelectionType)}
          >
            <option value="Random">Random</option>
            <option value="Selection">Selection</option>
          </select>
        </label>
        <RandomCluePlacementOption
          checked={randomCluePlacement}
          onChange={onRandomCluePlacementChange}
        />
        <ExpansionToggles
          includeBaseCards={true}
          enableDunwichBoard={enableDunwichBoard}
          enableKingsportBoard={enableKingsportBoard}
          enableInnsmouthBoard={enableInnsmouthBoard}
          onEnableDunwichBoardChange={onEnableDunwichBoardChange}
          onEnableKingsportBoardChange={onEnableKingsportBoardChange}
          onEnableInnsmouthBoardChange={onEnableInnsmouthBoardChange}
          includeDunwichCards={includeDunwichCards}
          includeDarkPharaohCards={includeDarkPharaohCards}
          includeKingsportCards={includeKingsportCards}
          includeKingInYellowCards={includeKingInYellowCards}
          includeBlackGoatCards={includeBlackGoatCards}
          includeMiskatonicCards={includeMiskatonicCards}
          includeLurkerCards={includeLurkerCards}
          includeInsmouthCards={includeInsmouthCards}
          includeFanMadeInvestigators={includeFanMadeInvestigators}
          disableBaseCards={true}
          showAdminOnly={showAdminOnly}
          onIncludeDunwichCardsChange={onIncludeDunwichCardsChange}
          onIncludeDarkPharaohCardsChange={onIncludeDarkPharaohCardsChange}
          onIncludeKingsportCardsChange={onIncludeKingsportCardsChange}
          onIncludeKingInYellowCardsChange={onIncludeKingInYellowCardsChange}
          onIncludeBlackGoatCardsChange={onIncludeBlackGoatCardsChange}
          onIncludeMiskatonicCardsChange={onIncludeMiskatonicCardsChange}
          onIncludeLurkerCardsChange={onIncludeLurkerCardsChange}
          onIncludeInsmouthCardsChange={onIncludeInsmouthCardsChange}
          onIncludeFanMadeInvestigatorsChange={onIncludeFanMadeInvestigatorsChange}
        />
        {includeInsmouthCards && (
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={usePersonalStories}
              onChange={event => onUsePersonalStoriesChange(event.target.checked)}
            />
            Use Personal Stories
          </label>
        )}
        {includeLurkerCards && (
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={useNewGateMarkers}
              onChange={event => onUseNewGateMarkersChange?.(event.target.checked)}
            />
            Use New Gate Markers
          </label>
        )}
        <div className="create-game-actions">
          <button onClick={onCreate} disabled={loading}>
            {loading ? 'Creating...' : 'Create Game'}
          </button>
          <button className='glass-button' onClick={onBack}>Back</button>
        </div>
        {error && <ErrorMessage error={error} />}
      </div>
    </div>
  );
};
